import { Builder } from '@builder.io/react';
import React from 'react';
import type { MediaObject } from 'schema-dts';
import { twMerge } from 'tailwind-merge';

import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import ImageFallback from '../elements/ImageFallback/ImageFallback';
import LinkWrapper from '../elements/LinkWrapper/LinkWrapper';
import ProgressiveImage from '../elements/ProgressiveImage/ProgressiveImage';
import StructuredData from '../elements/StructuredData';

export interface MediaTypeChipProps {
  title?: string;
  imageUrl?: string;
  imageAlt?: string;
  assetCount?: string;
  showAssetCount?: boolean;
  className?: string;
  link?: string;
}

function MediaTypeChip({
  title = 'Media Type',
  imageUrl = '',
  imageAlt = 'Media type icon',
  assetCount = '0 results',
  showAssetCount = true,
  className = '',
  link,
}: MediaTypeChipProps) {
  const accessibleAltText = imageAlt || `${title} icon` || 'Media type icon';

  const structuredData: MediaObject = {
    '@type': 'MediaObject',
    name: title,
    description: showAssetCount ? assetCount : undefined,
    thumbnailUrl: imageUrl || undefined,
  };

  const baseClasses =
    'inline-flex items-center gap-2 bg-white rounded-md-ds p-2 pr-4 border border-gray-400 min-w-fit';
  const interactiveClasses = link
    ? 'focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-opacity-50'
    : '';

  return (
    <>
      <StructuredData data={structuredData} />
      <LinkWrapper link={link}>
        <article
          className={twMerge(baseClasses, interactiveClasses, className)}
          aria-label={`${title}${
            showAssetCount && assetCount ? ` - ${assetCount}` : ''
          }`}
        >
          <div className="flex-shrink-0 w-14 h-14 relative">
            <ProgressiveImage
              src={imageUrl}
              alt={accessibleAltText}
              width={56}
              height={56}
              className="object-cover rounded-lg w-full h-full"
              sizes="56px"
              loading="lazy"
              shouldLazyLoad
              containerClassName="relative w-full h-full overflow-hidden rounded-lg"
              style={{
                borderRadius: '10px',
              }}
              fallbackComponent={
                <ImageFallback
                  width={56}
                  height={56}
                  style={{
                    borderRadius: '10px',
                  }}
                />
              }
            />
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <Text
              asChild
              fontSize="base"
              fontWeight="semibold"
              fontFamily="polysans"
            >
              <h3
                className="text-gray-900 whitespace-nowrap my-0"
                title={title}
              >
                {title}
              </h3>
            </Text>
            {showAssetCount && assetCount && (
              <Text
                asChild
                fontSize="sm"
                fontWeight="regular"
                fontFamily="polysans"
              >
                <p
                  className="text-gray-700"
                  aria-label={`Asset count: ${assetCount}`}
                  title={assetCount}
                >
                  {assetCount}
                </p>
              </Text>
            )}
          </div>
        </article>
      </LinkWrapper>
    </>
  );
}

export default MediaTypeChip;

Builder.registerComponent(MediaTypeChip, {
  name: 'Media Type Chip',
  description: 'A media type chip component',
  inputs: [
    {
      name: 'title',
      type: 'string',
      defaultValue: 'Premiere Pro Templates',
      helperText: 'The main title displayed on the chip',
    },
    {
      name: 'imageUrl',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      defaultValue: '',
      helperText: 'Square format image (recommended size: 48x48px or larger)',
    },
    {
      name: 'imageAlt',
      type: 'string',
      defaultValue: 'Media type icon',
      helperText: 'Alt text for the image (for accessibility)',
    },
    {
      name: 'showAssetCount',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Toggle to show or hide the asset count',
    },
    {
      name: 'assetCount',
      type: 'string',
      defaultValue: '60,690 results',
      helperText: 'Number of assets or results (e.g., "60,690 results")',
      showIf: 'options.get("showAssetCount") === true',
    },
    {
      name: 'link',
      type: 'string',
      defaultValue: '',
      helperText:
        'Optional URL to make the chip clickable (internal or external)',
    },
  ],
});
